import React from 'react';
import { Form, Field } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';

import { Box, Button, RFFReactSelect, TextAreaInput } from '@qwealth/qcore';
import { selectUser } from '@qwealth/qdata';

import { patchKyp } from 'data/data-layer/kyp';
import { Kyp, KypSelectableApprovedForValues, PMCantApproveKypTypeValues } from 'data/models/Kyp';
import { selectKyps } from 'data/selectors/kypSelectors';
import { setKyps, setSelectedKyp } from 'data/actions/KYP';
import TransitionBox from 'components/Households/Common/QNotes/TransitionBox';

interface KypFormProps {
  kyp: Kyp;
  onClose: () => void;
}

const KypReviewForm: React.FC<KypFormProps> = ({ kyp, onClose }) => {
  const { firstName, lastName } = useSelector(selectUser);
  const kyps = useSelector(selectKyps);
  const today = format(new Date(), 'MMMM d, yyyy');
  const dispatch = useDispatch();

  const handleSubmit = async (values: Kyp) => {
    if (kyp.type in PMCantApproveKypTypeValues) return;
    values.reviewedBy = firstName + ' ' + lastName;
    values.reviewDate = Date.now().toString();
    values.reviewStatus = 'approved';
    values.isApproved = 1;
    values.approvedFor = values.approvedFor ? values.approvedFor : 'firm-wide';

    const patchedKyp = await patchKyp(values, dispatch);

    if (patchedKyp) {
      const updatedKyps = kyps.map(product => (product.uuid === kyp.uuid ? patchedKyp : product));
      dispatch(setKyps(updatedKyps));
      dispatch(setSelectedKyp(patchedKyp));
      onClose();
    }
  };

  return (
    <TransitionBox index={2} isVisible={true}>
      <Form
        onSubmit={handleSubmit}
        initialValues={kyp}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Box display="flex" flexDirection="row" flexWrap="wrap" gap="largest" marginBottom="large">
              <Box width="20vh">
                <Box textStyle="formLabel">Approved For*</Box>
                <RFFReactSelect
                  name="approvedFor"
                  inputFieldProps={{
                    options: KypSelectableApprovedForValues,
                  }}
                />
              </Box>

              <Box>
                <Box fontWeight="bold" marginBottom="default">
                  Reviewer
                </Box>
                <Box>{firstName + ' ' + lastName}</Box>
              </Box>

              <Box>
                <Box fontWeight="bold" marginBottom="default">
                  Review Date
                </Box>
                <Box>{today}</Box>
              </Box>
            </Box>

            <Box marginTop="default">
              <Box fontWeight="bold" marginBottom="default">
                Assessment
              </Box>
              <Field name="assessment">
                {({ input, meta }) => (
                  <Box>
                    <TextAreaInput
                      rows={6}
                      value={input?.value ? input.value : ''}
                      onChange={e => input.onChange(e.target.value)}
                      width="100%"
                      onBlur={() => {
                        input.onBlur();
                      }}
                    />
                    {meta.error && meta.touched && <Box color="error">{meta.error}</Box>}
                  </Box>
                )}
              </Field>
            </Box>

            <Box display="flex" flexDirection="row" width="100%" justifyContent="right" marginTop="large" gap="default">
              <Button type="cancel" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">Approve</Button>
            </Box>
          </form>
        )}
      />
    </TransitionBox>
  );
};

export default KypReviewForm;
